import type { CommentableContentObject, Program, ProgramTimelineContentObject } from 'models';
import { can } from 'old/utils/permissions';
import { useTranslation } from 'react-i18next';
import { canCommentLegacyContentObject } from 'permissions';
import { Button, Chat3LineIcon, Flex, Text } from 'ui';
import { useUser } from 'features/auth';
import { CommentsWrapper } from 'features/feed';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- The legacy program timeline is built on the feed queries
import { useContentObjectCache } from 'features/feed/queries/cache';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Not re-exported from the features/programs barrel's public surface
import Rating from 'features/programs/feed/Rating';

type P = {
  isProgramPublished: boolean;
  item: ProgramTimelineContentObject;
  program: Program;
};

/**
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const Footer = ({ isProgramPublished, item, program }: P) => {
  const cache = useContentObjectCache();
  const { railsUserData: viewer } = useUser();
  const canComment = canCommentLegacyContentObject({
    /* eslint-disable @typescript-eslint/naming-convention -- Matches canCommentLegacyContentObject's legacy snake_case input shape */
    contentObject: {
      contentable_type: item.contentableType,
      organization: { id: item.organization.id },
      program: {
        id: item.program.id,
        settings: item.program.settings ? { allow_comments: item.program.settings.allowComments } : undefined,
      },
      type: item.type,
    },
    /* eslint-enable @typescript-eslint/naming-convention -- End: canCommentLegacyContentObject's legacy snake_case input shape */
    viewer,
  });
  const hasVisibleComments = item.commentsCount > 0;
  const { t } = useTranslation();

  return (
    <>
      {hasVisibleComments || isProgramPublished ? (
        <Flex className="shared-footer__likes">
          {hasVisibleComments ? (
            <Button icon={<Chat3LineIcon />} size="large" type="text">
              <Text type="secondary">{t('commentsCount', { count: item.commentsCount })}</Text>
            </Button>
          ) : null}

          {isProgramPublished ? (
            <div className="shared-footer__rating">
              <Rating
                canRate={Boolean(can(item, 'Program', 'rateProgramObject'))}
                canViewRating={Boolean(can(program, 'Program', 'seeFuturePosts'))}
                contentObjectId={item.id}
                // eslint-disable-next-line @typescript-eslint/require-await -- Rating's onRate prop requires an async function
                onRate={async (value) => {
                  cache.patch(item, (contentObject) => ({ ...contentObject, evaluation: value }));
                }}
                rating={item.evaluation ?? 0}
                ratingAverage={item.evaluationRatingAverage ?? ''}
              />
            </div>
          ) : null}
        </Flex>
      ) : null}

      {canComment || hasVisibleComments ? (
        <div className="shared-footer__comments-wrapper">
          <CommentsWrapper contentObject={item satisfies CommentableContentObject} />
        </div>
      ) : null}
    </>
  );
};

export default Footer;
